import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Col, Row } from 'antd';

import Contacts from 'components/Contacts/Contacts';
import CountryPreview from 'components/CountryPreview';
import EducationWorkExperience from 'components/EducationWorkExperience/EducationWorkExperience';
import JobseekerAvatar from 'components/JobseekerAvatar/JobseekerAvatar';
import LanguageWithLevel from 'components/Jobseekers/components/LanguageWithLevel/LanguageWithLevel';
import Tags from 'components/Jobseekers/components/Tags';
import LocationDisplay from 'components/LocationDisplay/LocationDisplay';
import MatchingTag from 'components/MatchingTag/MatchingTag';
import SidebarNavigation from 'components/SidebarNavigation';
import StatusIcon from 'components/StatusIcon/StatusIcon';
import StatusLongTag from 'components/StatusLongTag/StatusLongTag';

import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import {
  selectActiveJobseeker,
  selectJobseekers
} from 'features/jobseekers/jobseekersSelectors';
import { selectActiveUserId } from 'features/sidebar/sidebarSelectors';
import { setActiveUserId } from 'features/sidebar/sidebarSlice';

import { Dictionaries, JobseekerAttachmentTypes } from 'constants/enums';
import { JOBSEEKERS } from 'constants/path';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import styles from './JobseekerView.module.scss';

const JobseekerView: FC = () => {
  const { t } = useTranslation();
  const jobseekerId = useSelector(selectActiveUserId);
  const jobseeker = useSelector(selectActiveJobseeker);
  const jobseekers = useSelector(selectJobseekers);
  const jobseekerIndustries = useSelector(
    selectDictionary(Dictionaries.Industry)
  );
  const jobseekerSkillsets = useSelector(
    selectDictionary(Dictionaries.Skillset)
  );
  const jobShiftOptions = useSelector(selectDictionary(Dictionaries.JobShift));
  const jobTypeOptions = useSelector(selectDictionary(Dictionaries.JobType));

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const citizenship = jobseeker?.citizenship || null;

  useEffect(() => {
    const firstPathPart = pathname.split('/')?.[1];
    const isJobseekersRoute = firstPathPart === JOBSEEKERS.slice(1);

    if (isJobseekersRoute) {
      navigate(`${JOBSEEKERS}/${jobseekerId}`);
    }

    return () => {
      if (isJobseekersRoute) {
        navigate(JOBSEEKERS);
      }
    };
  }, [jobseekerId, navigate, pathname]);

  const isShowContactsSection =
    jobseeker?.jobseekerEmail || jobseeker?.jobseekerPhone;

  const jobseekerPhotoUrlEndpoint = jobseeker?.attachments?.find(
    (attachment) => attachment.type === JobseekerAttachmentTypes.JobseekerPhoto
  )?.url;

  return (
    <aside>
      <SidebarNavigation
        id={jobseekerId}
        data={jobseekers}
        action={setActiveUserId}
      />
      {!jobseeker ? (
        <div>{t('common.loading')}</div>
      ) : (
        <>
          <Row className={styles.row}>
            <Col span={24}>
              <JobseekerAvatar
                photoUrlEndpoint={jobseekerPhotoUrlEndpoint ?? null}
                key={jobseekerPhotoUrlEndpoint}
              />
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={24}>
              <div className={styles.title}>{jobseeker.fullName}</div>
            </Col>
          </Row>

          <MatchingTag
            matchingScore={jobseeker.matchScore}
            jobName={jobseeker.matchScoreJobTitle}
            className={styles.matchingTag}
          />

          {isShowContactsSection && (
            <>
              <div className={styles.sectionHeader}>
                {t('sidebar.jobseeker.view.contactInfo')}
              </div>
              <Row>
                <div className={styles.divider} />
              </Row>
              <div className={styles.sectionContentWrapper}>
                <Row className={styles.row}>
                  <Col span={24}>
                    <Contacts
                      phone={jobseeker?.jobseekerPhone}
                      email={jobseeker?.jobseekerEmail}
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}

          <div className={styles.sectionHeader}>
            {t('sidebar.jobseeker.view.bio')}
          </div>
          <Row>
            <div className={styles.divider} />
          </Row>

          <div className={styles.sectionContentWrapper}>
            <Row className={styles.row}>
              <Col span={24}>
                <LocationDisplay
                  location={jobseeker.location}
                  countryData={jobseeker.country}
                />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.location')}
                </div>
              </Col>
            </Row>
            <Row className={styles.row}>
              <Col span={24}>
                {citizenship ? (
                  <CountryPreview
                    countryCode={citizenship.code}
                    countryName={citizenship.name}
                  />
                ) : (
                  FALLBACK_VALUE
                )}
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.citizenship')}
                </div>
              </Col>
            </Row>
            <Row className={styles.row}>
              <Col span={24}>
                <Tags
                  dictionary={jobseekerIndustries}
                  values={jobseeker.industries}
                />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.industry')}
                </div>
              </Col>
            </Row>
            <Row className={styles.row}>
              <Col span={24}>
                <Tags
                  dictionary={jobseekerSkillsets}
                  values={jobseeker.skillsets}
                />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.skillset')}
                </div>
              </Col>
            </Row>
            <Row className={styles.row}>
              <Col span={24}>
                <LanguageWithLevel langs={jobseeker.languages} />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.languages')}
                </div>
              </Col>
            </Row>

            <EducationWorkExperience jobseekerId={jobseeker.id} />
          </div>

          <div className={styles.sectionHeader}>
            {t('sidebar.jobseeker.view.screeningInfo')}
          </div>
          <Row className={styles.row}>
            <div className={styles.divider} />
          </Row>

          <div className={styles.sectionContentWrapper}>
            <Row className={styles.row}>
              <Col span={12}>
                <Tags
                  isTextMode
                  dictionary={jobTypeOptions}
                  values={jobseeker.schedule}
                />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.preferredSchedule')}
                </div>
              </Col>
              <Col span={12}>
                <Tags
                  isTextMode
                  dictionary={jobShiftOptions}
                  values={jobseeker.preferredSchedule}
                />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.preferredShift')}
                </div>
              </Col>
            </Row>
            <Row className={styles.row}>
              <Col span={12}>
                <StatusIcon status={jobseeker.backgroundCheckConsent} />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.backgroundCheckConsent')}
                </div>
              </Col>
              <Col span={12}>
                <StatusIcon status={jobseeker.weekendOvertime} />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.availableForWeekends')}
                </div>
              </Col>
            </Row>
            <Row className={styles.row}>
              <Col span={24}>
                <StatusLongTag status={jobseeker.felonyConviction} />
                <div className={styles.label}>
                  {t('sidebar.jobseeker.view.felonyConviction')}
                </div>
              </Col>
            </Row>
          </div>
        </>
      )}
    </aside>
  );
};

export default JobseekerView;
